import type {
  ColumnReconcileResult,
  DataLoadStatusResponse,
  DataLoadTableResult,
  RowMismatchDetail,
  SalesforceObjectMeta,
  TableObjectMapping,
} from "../apiv2";

const POSTGRES_DB_TYPES = new Set(["postgres", "postgresql"]);

/**
 * UTC generation timestamp for client-side-generated deliverable
 * filenames (failure logs, CSV/PDF exports) -- sortable and filename-safe,
 * matching the backend's `_filename_timestamp()` (service.py /
 * sttm_generator.py) so every downloaded file's name captures when it was
 * produced, not just what it contains.
 */
export function filenameTimestamp(): string {
  return new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d+Z$/, "")
    .replace("T", "_");
}

/**
 * 2026-09-06, requested by user: "Limit Fields" checkbox picker's
 * paste-a-list alternative — splits a pasted column list (one per line, or
 * comma/tab/semicolon-separated, e.g. copied straight out of a
 * source-system field doc) and matches it case-insensitively against this
 * table's actual source columns. A pasted name that doesn't match anything
 * (typo, wrong table, ...) is silently dropped rather than staged for
 * selection, same as a copy-paste mistake would be from the checkbox list
 * itself.
 */
export function parsePastedColumnNames(
  text: string,
  availableColumns: string[],
): Set<string> {
  const pasted = new Set(
    text
      .split(/[\n,\t;]+/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean),
  );
  const actual = new Set(availableColumns.map((c) => c.toUpperCase()));
  return new Set(Array.from(pasted).filter((name) => actual.has(name)));
}

/**
 * 2026-09-02, reported by user: "why is the target schema name not right
 * it should be based on my target selected" — a connection's schema_name
 * field defaults to "public" in ConnectDatabaseDialog's form (a
 * Postgres-only convention) regardless of which dialect was actually
 * picked, so activating/creating a non-Postgres connection (Oracle,
 * Snowflake, Databricks, ...) that was never given its own schema name
 * blindly carried that literal "public" into ddlTargetSchema, overwriting
 * a real value like "MIGRATION" — mirrors the backend's
 * `_effective_source_schema` (service.py), applied here to the frontend's
 * own schema-name state instead of a SQL query. Returns "" (treat as
 * unset) for a non-Postgres dbType whose schema is exactly "public";
 * returns schemaName unchanged otherwise.
 */
export function effectiveSchemaNameFromConnection(
  schemaName: string,
  dbType: string,
): string {
  if (POSTGRES_DB_TYPES.has(dbType.trim().toLowerCase())) return schemaName;
  return schemaName.trim().toLowerCase() === "public" ? "" : schemaName;
}

export interface BulkMappingPair {
  sourceTable: string;
  targetObject: string;
}

/**
 * Builds one mapping pair per bulk-selected source/target, matched
 * positionally by selection order. When one side has a single selection
 * and the other has several (e.g. 2 source tables against 1 target
 * object), the lone value is broadcast across every pair instead of
 * leaving the extra pairs with an empty, effectively-dropped side —
 * every selected table must show up as its own pair.
 */
export function buildBulkMappingPairs(
  bulkSourceTables: string[],
  bulkTargetObjects: string[],
): BulkMappingPair[] {
  const count = Math.max(bulkSourceTables.length, bulkTargetObjects.length);
  return Array.from({ length: count }, (_, i) => ({
    sourceTable:
      bulkSourceTables[i] ??
      (bulkSourceTables.length === 1 ? bulkSourceTables[0] : ""),
    targetObject:
      bulkTargetObjects[i] ??
      (bulkTargetObjects.length === 1 ? bulkTargetObjects[0] : ""),
  }));
}

/**
 * Scopes which source tables get an auto-suggested mapping when landing on
 * the mapping step. Selecting exactly one table still lands on a single
 * mapping to build trust in before mapping more via "Map All Remaining
 * Tables" — but selecting more than one table already signals intent to map
 * them all, so every selected table is scoped in immediately instead of
 * silently leaving the rest pending.
 */
export function scopeTablesForInitialAutoMap<T extends { table_name: string }>(
  tables: T[],
  selectedTableNames: Set<string>,
): T[] {
  const selected = tables.filter((t) => selectedTableNames.has(t.table_name));
  return selected.length > 1 ? selected : selected.slice(0, 1);
}

// 2026-09-01, requested by user: the "Source → Target Mapping Pairs" picker
// defaults to a single src → target pair — mapping several at once is an
// explicit opt-in (a "Map multiple tables/objects at once" toggle), not the
// default. When that toggle is off, any multi-select change is capped down
// to just the most-recently-picked value instead of accumulating a list.
export function capMappingSelectionForMode(
  values: string[],
  bulkMappingMode: boolean,
): string[] {
  return bulkMappingMode ? values : values.slice(-1);
}

export interface FailedTableRetryPlan {
  /** Indices into the `mappings` array that were resolved and can be retried. */
  failedIdx: number[];
  /** Count of failed results that could not be matched back to a mapping. */
  unmatchedCount: number;
  /** The failed results themselves that couldn't be matched back to a
   * mapping — kept (not just counted) so callers can carry them forward
   * into the Load Results panel instead of letting them silently vanish,
   * since they're neither retried nor otherwise represented anywhere. */
  unmatchedResults: DataLoadTableResult[];
}

/**
 * Resolves which failed load results can be retried by mapping them back to
 * `mappings` indices. Results that can't be matched (findMappingIndex returns
 * -1) are excluded from `failedIdx` but returned in `unmatchedResults` (and
 * counted in `unmatchedCount`) so callers can surface/carry them forward
 * instead of silently dropping them.
 */
export function resolveFailedTableRetryPlan(
  tableResults: DataLoadTableResult[],
  findMappingIndex: (result: DataLoadTableResult) => number,
): FailedTableRetryPlan {
  const failedResults = tableResults.filter((r) => r.error);
  const failedIdx: number[] = [];
  const unmatchedResults: DataLoadTableResult[] = [];
  failedResults.forEach((r) => {
    const idx = findMappingIndex(r);
    if (idx >= 0) {
      failedIdx.push(idx);
    } else {
      unmatchedResults.push(r);
    }
  });
  return {
    failedIdx,
    unmatchedCount: unmatchedResults.length,
    unmatchedResults,
  };
}

/**
 * "Retry Failed Tables" starts a brand-new load job scoped to only the
 * previously-failed tables -- its status response's `table_results` (and
 * aggregate counts) therefore only ever cover that retried subset, never
 * the tables that already succeeded in the original run. Folding the
 * carried-forward (previously successful) results back in here keeps the
 * Load Results panel showing every table from the original run instead of
 * silently dropping the successful ones each time a retry job is polled.
 */
export function mergeCarryForwardIntoLoadStatus(
  carryForward: DataLoadTableResult[],
  status: DataLoadStatusResponse,
): DataLoadStatusResponse {
  if (carryForward.length === 0) return status;
  const carryRowsLoaded = carryForward.reduce(
    (sum, r) => sum + r.rows_loaded,
    0,
  );
  const carryRowsFailed = carryForward.reduce(
    (sum, r) => sum + r.rows_failed,
    0,
  );
  return {
    ...status,
    // Tag each carried-forward row so the UI can make clear it wasn't
    // actually reprocessed by this (retry) job -- see carried_forward's
    // own doc comment in apiv2.ts.
    table_results: [
      ...carryForward.map((r) => ({ ...r, carried_forward: true })),
      ...status.table_results,
    ],
    total_tables: carryForward.length + status.total_tables,
    tables_done: carryForward.length + status.tables_done,
    total_rows_loaded: carryRowsLoaded + status.total_rows_loaded,
    total_rows_failed: carryRowsFailed + status.total_rows_failed,
  };
}

/**
 * Which step the "Data Loader" action's Resume dialog should land on, now
 * that the old standalone "Load to Target" action's `sf_load` step is one
 * of Data Loader's own steps (`ddl` / `load` / `sf_load`) instead of a
 * separate 4th action.
 *
 * A project that only ever used the old standalone "Load to Target"
 * action (never touched DDL/staging load under the Data Loader action)
 * must still resume straight into `"sf_load"` — exactly where the old
 * 4-action UI would have landed it. Any project with `hasDdlState` or
 * `hasLoadJob` present (the common case, including a project that used
 * both old actions) lands on `"ddl"` first instead, matching Data
 * Loader's own pre-fold resume behavior unchanged.
 */
export function resolveLoaderResumeStep(
  hasDdlState: boolean,
  hasLoadJob: boolean,
): "ddl" | "sf_load" {
  return hasDdlState || hasLoadJob ? "ddl" : "sf_load";
}

/** Column headers for a Column-by-Column Reconciliation export (PDF/Excel)
 * — kept alongside reconcileColumnExportRows() so the two can never drift
 * out of positional sync with each other. */
export const RECONCILE_COLUMN_EXPORT_HEADERS = [
  "Source Column",
  "Target Field",
  "Source Type",
  "Target Type",
  "Source Nulls",
  "Target Nulls",
  "Source Distinct",
  "Target Distinct",
  "Status",
];

/** Flattens one table's column reconciliation results into export rows
 * matching RECONCILE_COLUMN_EXPORT_HEADERS — shared by the PDF and Excel
 * download buttons so both always export the identical data. */
export function reconcileColumnExportRows(
  columns: ColumnReconcileResult[],
): (string | number)[][] {
  return columns.map((c) => [
    c.source_column,
    c.target_field,
    c.source_type,
    c.target_type,
    c.source_null_count ?? "—",
    c.target_null_count,
    c.source_distinct_count ?? "—",
    c.target_distinct_count ?? "—",
    c.status === "match" ? "Match" : "Review",
  ]);
}

/** Column headers for a Row-by-Row Reconciliation mismatch export
 * (PDF/Excel) — kept alongside reconcileRowExportRows() so the two can
 * never drift out of positional sync with each other. One export row per
 * (mismatched key, differing column) pair, since a single row can have
 * more than one differing column. */
export const RECONCILE_ROW_EXPORT_HEADERS = [
  "Key",
  "Source Column",
  "Target Field",
  "Source Value",
  "Target Value",
];

/** Flattens a table's row-mismatch details into export rows matching
 * RECONCILE_ROW_EXPORT_HEADERS — shared by the PDF and Excel download
 * buttons so both always export the identical data. */
export function reconcileRowExportRows(
  mismatches: RowMismatchDetail[],
): (string | number)[][] {
  const rows: (string | number)[][] = [];
  for (const m of mismatches) {
    const keyLabel = Object.entries(m.key)
      .map(([k, v]) => `${k}=${v}`)
      .join(", ");
    if (m.differing_columns.length === 0) {
      rows.push([keyLabel, "—", "—", "—", "—"]);
      continue;
    }
    for (const d of m.differing_columns) {
      rows.push([
        keyLabel,
        d.source_column,
        d.target_field,
        d.source_value ?? "—",
        d.target_value ?? "—",
      ]);
    }
  }
  return rows;
}

// Splits a comma-separated SQL fragment at its TOP-LEVEL commas only —
// commas nested inside parentheses (e.g. a function call's argument list)
// don't count as separators. Used by formatSqlForDisplay below to lay out
// a SELECT's column list / an INSERT's column and VALUES lists one per
// line without splitting something like `SUM(a, b)` in half.
function splitTopLevelSqlCommas(fragment: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = "";
  for (const ch of fragment) {
    if (ch === "(") depth++;
    else if (ch === ")") depth--;
    if (ch === "," && depth === 0) {
      parts.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  if (current.trim()) parts.push(current);
  return parts.map((p) => p.trim()).filter(Boolean);
}

// Computes [start, end) character ranges covered by single-quoted SQL
// string literals — a doubled '' inside a literal is the standard SQL
// escape for a literal single quote, not the end of the string. Used by
// formatSqlForDisplay below so a clause keyword matched inside quoted
// data (e.g. `WHERE region = 'orders from west'`) is never mistaken for
// a real FROM/WHERE/... clause boundary.
function findStringLiteralRanges(sql: string): [number, number][] {
  const ranges: [number, number][] = [];
  let i = 0;
  while (i < sql.length) {
    if (sql[i] === "'") {
      const start = i;
      i++;
      while (i < sql.length) {
        if (sql[i] === "'") {
          if (sql[i + 1] === "'") {
            i += 2;
            continue;
          }
          i++;
          break;
        }
        i++;
      }
      ranges.push([start, i]);
    } else {
      i++;
    }
  }
  return ranges;
}

/**
 * 2026-09-02, requested by user: the Loader Script preview showed
 * build_loader_sql_preview's SELECT/INSERT SQL exactly as generated —
 * correct, but a single unbroken line (or close to it) rather than
 * something a person would actually write by hand. Reformats it into
 * conventional multi-line SQL (SELECT's columns and an INSERT's
 * column/VALUES lists one per line, indented; FROM/JOIN/ON/WHERE/GROUP
 * BY/ORDER BY each on their own line) purely for display — it's re-parsed
 * from scratch either way (server-side re-validation on save, or a fresh
 * server-side preview call), so reformatting whitespace here changes
 * nothing about what actually runs.
 *
 * Deliberately conservative: only reformats a string that's unambiguously
 * one of the two shapes this preview endpoint (or a user's own custom
 * SELECT/WITH override) can produce; anything else is returned byte-for-
 * byte unchanged rather than risk mangling it.
 */
export function formatSqlForDisplay(sql: string): string {
  if (!sql) return sql;
  const trimmed = sql.trim();

  const insertMatch = trimmed.match(
    /^INSERT INTO\s+(.+?)\s*\((.+)\)\s*VALUES\s*\((.+)\)\s*$/is,
  );
  if (insertMatch) {
    const [, tableRef, colsRaw, valsRaw] = insertMatch;
    const cols = splitTopLevelSqlCommas(colsRaw);
    const vals = splitTopLevelSqlCommas(valsRaw);
    if (cols.length === 0 || cols.length !== vals.length) return sql;
    const lines = [`INSERT INTO ${tableRef.trim()} (`];
    cols.forEach((c, i) =>
      lines.push(`    ${c}${i < cols.length - 1 ? "," : ""}`),
    );
    lines.push(")", "VALUES (");
    vals.forEach((v, i) =>
      lines.push(`    ${v}${i < vals.length - 1 ? "," : ""}`),
    );
    lines.push(")");
    return lines.join("\n");
  }

  // Only the plain-SELECT shape (never generated with a leading WITH/CTE)
  // is reformatted below — a hand-written WITH/CTE custom_load_sql is
  // returned untouched rather than risk the naive keyword-position search
  // below matching a SELECT/FROM/WHERE that's actually nested inside one
  // of the CTE's own subqueries.
  if (!/^SELECT\s/i.test(trimmed)) return sql;

  const CLAUSE_KEYWORDS = [
    "SELECT",
    "FROM",
    "INNER JOIN",
    "LEFT JOIN",
    "RIGHT JOIN",
    "WHERE",
    "GROUP BY",
    "ORDER BY",
  ];
  const clauseRe = new RegExp(`\\b(${CLAUSE_KEYWORDS.join("|")})\\b`, "gi");
  const literalRanges = findStringLiteralRanges(trimmed);
  const insideLiteral = (idx: number) =>
    literalRanges.some(([start, end]) => idx >= start && idx < end);
  const matches: { keyword: string; index: number; length: number }[] = [];
  let match: RegExpExecArray | null;
  while ((match = clauseRe.exec(trimmed)) !== null) {
    if (insideLiteral(match.index)) continue;
    matches.push({
      keyword: match[1].toUpperCase(),
      index: match.index,
      length: match[1].length,
    });
  }
  // No FROM/SELECT structure recognized (e.g. a WITH/CTE the user wrote by
  // hand) — leave it exactly as typed rather than guess at its shape.
  if (matches.length === 0 || matches[0].keyword !== "SELECT") return sql;

  const lines: string[] = [];
  matches.forEach((m, i) => {
    const bodyStart = m.index + m.length;
    const bodyEnd =
      i + 1 < matches.length ? matches[i + 1].index : trimmed.length;
    const body = trimmed.slice(bodyStart, bodyEnd).trim();
    if (m.keyword === "SELECT") {
      lines.push("SELECT");
      const cols = splitTopLevelSqlCommas(body);
      cols.forEach((c, ci) =>
        lines.push(`    ${c}${ci < cols.length - 1 ? "," : ""}`),
      );
    } else if (m.keyword.endsWith("JOIN")) {
      const onIdx = body.search(/\bON\b/i);
      if (onIdx >= 0) {
        lines.push(`${m.keyword} ${body.slice(0, onIdx).trim()}`);
        lines.push(`    ON ${body.slice(onIdx + 2).trim()}`);
      } else {
        lines.push(`${m.keyword} ${body}`);
      }
    } else {
      lines.push(`${m.keyword} ${body}`);
    }
  });
  return lines.join("\n");
}

/**
 * 2026-09-15, requested by user: "Sequential" load order should respect
 * FK/lookup dependencies declared on the TARGET schema instead of just the
 * order tables happen to appear in Mapping Summary. Reads each target
 * object's fields' `reference_to` (populated from a real RDBMS target's
 * is_foreign_key/fk_references by tablesToTargetObjects, or natively by a
 * CRM target's own lookup/master-detail fields) to build a dependency
 * graph among the tables actually being loaded (`idxs`, positions into
 * that filtered array), then returns a topologically-sorted
 * one-table-per-stage load_order — parent/referenced tables before the
 * children that look them up.
 *
 * Returns `undefined` (today's declaration-order default) when there's
 * nothing to sort by — fewer than 2 tables, no target object metadata at
 * all, or no dependency edges among the tables in this run — so behavior
 * is unchanged for CRM targets with no lookups and the "no target
 * connection" auto-generated model path.
 *
 * A dependency cycle (rare, but possible with self-referencing or
 * circular FKs) is broken by leaving any tables still unresolved after
 * the sort in their original relative order rather than failing the
 * whole computation.
 */
export function computeFkAwareSequentialOrder(
  idxs: number[],
  mappings: TableObjectMapping[],
  targetObjects: SalesforceObjectMeta[],
): number[][] | undefined {
  if (idxs.length <= 1 || targetObjects.length === 0) return undefined;

  const objectsByName = new Map(
    targetObjects.map((o) => [o.api_name.toLowerCase(), o]),
  );
  const positionsByTargetName = new Map<string, number[]>();
  idxs.forEach((origIdx, pos) => {
    const key = (mappings[origIdx]?.target_object ?? "").toLowerCase();
    if (!positionsByTargetName.has(key)) positionsByTargetName.set(key, []);
    positionsByTargetName.get(key)!.push(pos);
  });

  // dependsOn.get(pos) = positions that must load before `pos`.
  const dependsOn = new Map<number, Set<number>>();
  idxs.forEach((_, pos) => dependsOn.set(pos, new Set()));

  idxs.forEach((origIdx, pos) => {
    const targetName = (mappings[origIdx]?.target_object ?? "").toLowerCase();
    const meta = objectsByName.get(targetName);
    if (!meta) return;
    for (const field of meta.fields) {
      for (const refObject of field.reference_to ?? []) {
        const refKey = refObject.toLowerCase();
        if (refKey === targetName) continue; // self-referencing FK -- unorderable, ignore
        for (const depPos of positionsByTargetName.get(refKey) ?? []) {
          if (depPos !== pos) dependsOn.get(pos)!.add(depPos);
        }
      }
    }
  });

  const hasAnyDependency = Array.from(dependsOn.values()).some(
    (deps) => deps.size > 0,
  );
  if (!hasAnyDependency) return undefined;

  // Kahn's algorithm, stable: always pick the lowest-position ready node so
  // tables with no dependency between them keep their original relative
  // order.
  const remainingDeps = new Map(
    Array.from(dependsOn, ([pos, deps]) => [pos, new Set(deps)] as const),
  );
  const dependents = new Map<number, number[]>();
  idxs.forEach((_, pos) => dependents.set(pos, []));
  for (const [pos, deps] of dependsOn) {
    for (const dep of deps) dependents.get(dep)!.push(pos);
  }

  const ready: number[] = [];
  for (let pos = 0; pos < idxs.length; pos++) {
    if (remainingDeps.get(pos)!.size === 0) ready.push(pos);
  }

  const order: number[] = [];
  const visited = new Set<number>();
  while (ready.length > 0) {
    ready.sort((a, b) => a - b);
    const pos = ready.shift()!;
    if (visited.has(pos)) continue;
    visited.add(pos);
    order.push(pos);
    for (const dependent of dependents.get(pos) ?? []) {
      const deps = remainingDeps.get(dependent)!;
      deps.delete(pos);
      if (deps.size === 0 && !visited.has(dependent)) ready.push(dependent);
    }
  }
  // Any position left unresolved is part of a dependency cycle -- append in
  // original relative order rather than drop it from the load entirely.
  for (let pos = 0; pos < idxs.length; pos++) {
    if (!visited.has(pos)) order.push(pos);
  }

  return order.map((pos) => [pos]);
}

/** One row of the "Stream to DB" (issue #735) field-mapping editor. */
export interface CrmStreamFieldMappingRow {
  source_field: string;
  target_column: string;
  value_map: string;
}

/**
 * Seeds the "Stream to DB" modal's field mappings with a 1:1 name guess
 * (target column = uppercased source API name) for every field on the
 * chosen CRM/SaaS object, so the user edits an already-complete mapping
 * rather than building one from scratch.
 */
export function seedCrmStreamFieldMappings(
  obj: SalesforceObjectMeta,
): CrmStreamFieldMappingRow[] {
  return obj.fields.map((f) => ({
    source_field: f.api_name,
    target_column: f.api_name.toUpperCase(),
    value_map: "",
  }));
}

/**
 * Validates a "Stream to DB" submission before it reaches the network --
 * mirrors the checks the backend's `CrmToDbLoadRequest` would otherwise
 * reject with a 422, but surfaced immediately in the modal instead.
 * Returns an error message, or `null` if the submission is valid.
 */
export function validateCrmStreamLoadConfig(
  config: {
    targetTable: string;
    fieldMappings: CrmStreamFieldMappingRow[];
  },
  hasTargetConnection: boolean,
): string | null {
  if (!hasTargetConnection) {
    return "Connect to a target database first (Target Platform step below)";
  }
  if (!config.fieldMappings.some((m) => m.target_column.trim())) {
    return "At least one field mapping is required";
  }
  if (!config.targetTable.trim()) {
    return "Target table name is required";
  }
  return null;
}

/**
 * Drops field mappings the user cleared (empty target column) and turns an
 * empty `value_map` string into `undefined` so the request body only ever
 * carries a real value-map expression, never an empty-string one.
 */
export function buildCrmToDbFieldMappings(
  fieldMappings: CrmStreamFieldMappingRow[],
): { source_field: string; target_column: string; value_map?: string }[] {
  return fieldMappings
    .filter((m) => m.target_column.trim())
    .map((m) => ({
      source_field: m.source_field,
      target_column: m.target_column,
      value_map: m.value_map.trim() || undefined,
    }));
}

// Above this many table tabs the STTM review step shows a filter box and caps
// the tab bar's height (it scrolls instead) -- a 700-table project (MI SACWIS)
// otherwise renders a wall of tab buttons several screens tall above the
// active table.
export const STTM_TAB_FILTER_THRESHOLD = 12;

/**
 * Which STTM review tabs to render. `removed` holds soft-deleted mapping
 * indices. The filter only applies once there are more than
 * STTM_TAB_FILTER_THRESHOLD tabs, and the active tab always stays visible
 * so the table being edited never loses its tab. `total` counts the
 * non-removed tabs; `matching` counts those the query matches (all of them
 * when no query applies).
 */
export function sttmTabsToShow(
  mappings: Pick<
    TableObjectMapping,
    "target_label" | "target_object" | "source_table"
  >[],
  removed: ReadonlySet<number>,
  activeIdx: number,
  query: string,
): {
  filterable: boolean;
  visible: number[];
  total: number;
  matching: number;
} {
  const kept = mappings.map((_, i) => i).filter((i) => !removed.has(i));
  const filterable = kept.length > STTM_TAB_FILTER_THRESHOLD;
  const q = filterable ? query.trim().toLowerCase() : "";
  const matches = (i: number) => {
    if (!q) return true;
    const m = mappings[i];
    return (
      (m.target_label || "").toLowerCase().includes(q) ||
      m.target_object.toLowerCase().includes(q) ||
      m.source_table.toLowerCase().includes(q)
    );
  };
  return {
    filterable,
    visible: kept.filter((i) => i === activeIdx || matches(i)),
    total: kept.length,
    matching: kept.filter(matches).length,
  };
}
